import { PropsWithChildren, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { MdTripOrigin } from 'react-icons/md';
import { Divider, Listbox, ListboxItem, Popover, PopoverContent, Selection } from '@ds';
import { CCShiftOptionType } from '@lp/features/Scheduler/SchedulerClinicalCoordinators/types';
import {
  getShiftFromShiftOption,
  getShiftOptionFromShift,
  getShiftOptionIconColor,
} from '@lp/features/Scheduler/SchedulerClinicalCoordinators/utils';
import { CCShift, CCShiftSelectPayload } from '@/api/apis/resources/schedule';
import { NameByValue } from '@/types';

type CCShiftCellDropdownProps = PropsWithChildren<{
  title?: string;
  isPlacementBottom?: boolean;
  optionList: NameByValue<CCShiftOptionType>[];
  shiftData?: CCShift;
  onShiftChange: (shiftPayload?: CCShiftSelectPayload) => void;
  triggerRef: RefObject<HTMLButtonElement | null>;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  canReset?: boolean;
}>;

export const CCShiftCellDropdown = ({
  title = 'Change Status',
  isPlacementBottom,
  isOpen,
  onOpenChange,
  triggerRef,
  optionList,
  shiftData,
  onShiftChange,
  canReset,
}: CCShiftCellDropdownProps) => {
  const selectedShiftOption = getShiftOptionFromShift(shiftData);

  const onSelectionChange = (keys: Selection) => {
    if (keys !== 'all') {
      const selected = Array.from(keys)[0] as CCShiftOptionType | undefined;
      onShiftChange(getShiftFromShiftOption(selected));
      onOpenChange(false);
    }
  };

  return createPortal(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    <Popover
      placement={isPlacementBottom ? 'bottom-start' : 'right-start'}
      // @ts-expect-error HeroUI Popover triggerRef expects RefObject<HTMLElement>, React 19 provides RefObject<HTMLButtonElement | null>
      triggerRef={triggerRef}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      shouldCloseOnInteractOutside={(el) => el !== triggerRef.current && !triggerRef.current?.contains(el)}
    >
      <PopoverContent>
        <Listbox
          selectionMode="single"
          disallowEmptySelection={!canReset}
          selectedKeys={selectedShiftOption ? [selectedShiftOption] : undefined}
          onSelectionChange={onSelectionChange}
          aria-label="Change Shift Status"
          topContent={
            <>
              <span className="pl-2 text-s-m text-base-20">{title}</span>
              <Divider orientation="horizontal" />
            </>
          }
        >
          {optionList.map((optionItem) => (
            <ListboxItem
              key={optionItem.value}
              textValue={optionItem.name}
              startContent={<MdTripOrigin size={16} className={getShiftOptionIconColor(optionItem.value)} />}
            >
              {optionItem.name}
            </ListboxItem>
          ))}
        </Listbox>
      </PopoverContent>
    </Popover>,
    document.body,
  );
};
